import React from 'react';

export default function DocsModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-8 overflow-y-auto">
      <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-3xl w-full max-h-full overflow-y-auto p-8 relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕ Close
        </button>
        
        <h1 className="text-2xl font-bold text-emerald-400 mb-6">Architecture & Sync Mitigations</h1>
        
        <div className="space-y-6 text-gray-300 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-white mb-2">1. The Problem of Caption Drift</h2>
            <p>
              In browser-based video editors, keeping text perfectly synced with audio over long durations (3+ minutes) is notoriously difficult. If animations rely purely on accumulating <code>requestAnimationFrame</code> delta times, or on React component renders, microscopic frame drops and JavaScript thread starvation cause the visual state to drift away from the audio playhead.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">2. Single Authoritative Clock</h2>
            <p>
              To solve drift, our architecture enforces a <strong>Single Source of Truth</strong> for time. We rely on the Web Audio API's clock and the Wavesurfer instance's <code>getCurrentTime()</code>. 
            </p>
            <p className="mt-2">
              The <code>requestAnimationFrame</code> loop inside the Canvas renderer <strong>never tracks elapsed time itself</strong>. Instead, every frame it reads the current exact audio timestamp and deterministically renders the text state (karaoke highlight progress, animation scaling, alpha) for that precise millisecond. If the browser lags and drops 50 frames, the next drawn frame instantly jumps to the correct state with zero accumulated drift.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">3. Deterministic Animation</h2>
            <p>
              Animations like "Pop-in" and "Fade" do not use CSS transitions or stateful easing variables. They compute their current value purely mathematically based on the formula: 
              <br/><br/>
              <code className="bg-gray-800 p-1 rounded text-emerald-300">progress = currentTime - blockStartTime</code>
              <br/><br/>
              This ensures seeking backwards or forwards instantly resolves the correct animation state.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">4. Seamless Export with MediaRecorder</h2>
            <p>
              Exporting via <code>canvas.captureStream()</code> and Web Audio's <code>MediaStreamDestinationNode</code> can introduce sync issues if not routed correctly. By routing the master audio directly into the export graph <em>and</em> the speakers, and relying on Wavesurfer's linear playback to drive the canvas loop during the export phase, we guarantee the resulting WebM file has perfectly multiplexed Audio and Video tracks.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}